import {observer} from "mobx-react";
import {useInjection} from "inversify-react";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import {
    Body,
    Container,
    ContentRow,
    Footer,
    PaddingFilters
} from "./styled";
import FilterButton from "@dropDesk/presentation/components/filters/filter_button";
import React, {useEffect, useRef, useState} from "react";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import {SectorEntity} from "@dropDesk/domain/entities/sector/sector.entity";
import {ClientEntity} from "@dropDesk/domain/entities/client/client.entity";
import {
    TicketsDescriptionStaticEntity
} from "@dropDesk/domain/entities/ticket_description_static/ticket_description_static.entity";
import InputClientSearch from "@dropDesk/presentation/pages/ticket/ui/search/search_bar/inputs_client_search";
import InputContactSearch from "@dropDesk/presentation/pages/ticket/ui/search/search_bar/inputs_contact_search";
import InputSectorSearch from "@dropDesk/presentation/pages/ticket/ui/search/search_bar/inputs_sector_search";
import InputAttendantSearch from "@dropDesk/presentation/pages/ticket/ui/search/search_bar/inputs_attendant_search";
import InputProblemSearch from "@dropDesk/presentation/pages/ticket/ui/search/search_bar/inputs_problem_search";
import ButtonRemoveFilters from "@dropDesk/presentation/pages/ticket/ui/search/search_bar/button_remove_filters";

export type ReportEntityFilters = {
    idClient?: string;
    idContact?: string;
    idSector?: string;
    idUser?: string;
    idDescriptionStatic?: string;
};

const ReportEntityFiltersButton = observer(({
    filterClient,
    filterUserClient,
    filterSector,
    filterAttendant,
    filterDescriptionStatic,
    setFilterClient,
    setFilterUserClient,
    setFilterSector,
    setFilterAttendant,
    setFilterDescriptionStatic,
    loading,
    onClear,
    radius = 25,
    marginLeft = 10,
}: {
    filterClient: ClientEntity | null;
    filterUserClient: UserEntity | null;
    filterSector: SectorEntity | null;
    filterAttendant: UserEntity | null;
    filterDescriptionStatic: TicketsDescriptionStaticEntity | null;
    setFilterClient: (filter: ClientEntity | null) => void;
    setFilterUserClient: (filter: UserEntity | null) => void;
    setFilterSector: (filter: SectorEntity | null) => void;
    setFilterAttendant: (filter: UserEntity | null) => void;
    setFilterDescriptionStatic: (filter: TicketsDescriptionStaticEntity | null) => void;
    loading: boolean;
    onClear: () => void;
    radius?: number;
    marginLeft?: number;
}) => {
    const [visible, setVisible] = useState(false);
    const [zIndex, setZIndex] = useState<number>(0);
    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;
    const timeoutRef = useRef<number | undefined>(undefined);
    const useProblemStatic = !appController.isUserClient
        && !!appController.user?.company.configurations.ticket.useProblemStatic;

    useEffect(() => {
        if (visible) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            setZIndex(1300);
        } else {
            timeoutRef.current = window.setTimeout(() => {
                setZIndex(0);
            }, 200);
        }
    }, [visible]);

    const filterCount = [
        filterClient,
        filterUserClient,
        filterSector,
        filterAttendant,
        filterDescriptionStatic,
    ].filter(Boolean).length;

    const handleClearAll = () => {
        setVisible(false);
        onClear();
    };

    return (
        <FilterButton
            hasFilter={filterCount > 0}
            filterCount={filterCount}
            loading={loading}
            open={visible}
            onOpenChange={(open) => setVisible(open)}
            zIndex={zIndex}
            radius={radius}
            marginLeft={marginLeft}
            content={
                <Container border={colors.border}>
                    <Body>
                        <ContentRow>
                            <PaddingFilters>
                                <InputClientSearch
                                    setFilterClient={(client) => {
                                        setFilterClient(client);
                                        if (filterUserClient && client && filterUserClient.idClient !== client.id) {
                                            setFilterUserClient(null);
                                        }
                                        if (!client) {
                                            setFilterUserClient(null);
                                        }
                                    }}
                                    filterClient={filterClient}
                                    loading={loading}
                                />
                            </PaddingFilters>
                            <PaddingFilters style={{marginLeft: 16}}>
                                <InputContactSearch
                                    filterClient={filterClient}
                                    filterUserClient={filterUserClient}
                                    setFilterUserClient={(contact) => setFilterUserClient(contact)}
                                    loading={loading}
                                />
                            </PaddingFilters>
                        </ContentRow>

                        <ContentRow>
                            <PaddingFilters>
                                <InputSectorSearch
                                    setFilterSector={(sector) => setFilterSector(sector)}
                                    filterSector={filterSector}
                                    loading={loading}
                                />
                            </PaddingFilters>
                            <PaddingFilters style={{marginLeft: 16}}>
                                <InputAttendantSearch
                                    setFilterAttendant={(attendant) => setFilterAttendant(attendant)}
                                    filterAttendant={filterAttendant}
                                    loading={loading}
                                />
                            </PaddingFilters>
                        </ContentRow>

                        {useProblemStatic && (
                            <ContentRow>
                                <PaddingFilters>
                                    <InputProblemSearch
                                        setFilterDescriptionStatic={(problem) => setFilterDescriptionStatic(problem)}
                                        filterDescriptionStatic={filterDescriptionStatic}
                                        loading={loading}
                                    />
                                </PaddingFilters>
                            </ContentRow>
                        )}
                    </Body>
                    <Footer>
                        <ButtonRemoveFilters
                            onClick={handleClearAll}
                            loading={loading}
                        />
                    </Footer>
                </Container>
            }
        />
    );
});

export default ReportEntityFiltersButton;
